<template>
  <div class="asc-issue-detail--table">
    <div class="asc-issue-detail--table-column">
      <h3 class="asc-issue-detail--table-column-header">AIRCRAFT DETAILS</h3>
      <ul class="asc-issue-detail--table-column-list">
        <li class="detail asc-aircraft-detail--tail">
          <span class="label">Tail:</span>
          <span class="detail">{{ tail }}</span>
        </li>
        <li class="asc-aircraft-detail--model">
          <span class="label">Model:</span>
          <span class="detail">{{ model }}</span>
        </li>
        <li class="asc-aircraft-detail--system">
          <span class="label">System:</span>
          <span class="detail">{{ system }}</span>
        </li>
        <li class="asc-aircraft-detail--component">
          <span class="label">Component:</span>
          <span class="detail">{{ component }}</span>
        </li>
        <li class="asc-aircraft-detail--part-no">
          <span class="label">Part #:</span>
          <span class="detail">{{
            data.partNumber ? data.partNumber : 'N/A'
          }}</span>
        </li>
      </ul>
    </div>
    <div class="asc-issue-detail--table-column">
      <h3 class="asc-issue-detail--table-column-header">ALERT DETAILS</h3>
      <ul class="asc-issue-detail--table-column-list">
        <li
          v-if="
            (isEditPermissionInvestigate || isReadOnlyPermissionInvestigate) &&
            !isRecommendationTab &&
            showAlertDetails
          "
          class="asc-alert-detail--event-of-interest"
        >
          <span class="label">Event of Interest:</span>
          <span class="detail">{{ earliestAlert }}</span>
        </li>
        <li v-if="showAlertDetails" class="asc-alert-detail--analytic">
          <span class="label">Analytic:</span>
          <span class="detail">{{ analyticModuleName }}</span>
        </li>
        <li v-if="showAlertDetails" class="asc-alert-detail--first-alert-date">
          <span class="label">First Alert Date:</span>
          <span class="detail">{{ hasAlerts ? firstAlertDate : 'N/A' }}</span>
        </li>
        <li class="asc-alert-detail--alert-count">
          <span class="label">Alert Count:</span>
          <span class="detail"
            >{{ hasAlerts ? hasAlerts : 'N/A'
            }}<asc-link-button
              v-if="showViewAlertsLink"
              class="detail--link-button pt-0"
              text="View Alerts"
              :uppercase="false"
              :hover="false"
              @click="handleViewPHMAlerts"
            ></asc-link-button>
          </span>
        </li>
        <li v-if="isCustomAlert">
          <span class="label">Severity:</span>
          <span class="detail">{{ getCustomAlertSeverity }}</span>
        </li>
        <li
          v-if="
            (isEditPermissionInvestigate || isReadOnlyPermissionInvestigate) &&
            !isRecommendationTab
          "
          class="asc-alert-detail--severity"
        >
          <span class="label">Severity:</span>
          <span class="detail">
            <asc-autocomplete-selector
              v-model="getSeverity"
              display-clear
              no-array
              :disabled="disabled"
              :items="severityOptions"
              :readonly="isReadOnlyPermissionInvestigate"
              :sort-items="false"
              class="hide-details"
            >
            </asc-autocomplete-selector>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import AscAutocompleteSelector from '@/components/molecules/AscAutocompleteSelector'
import AscLinkButton from '@/components/atoms/AscLinkButton'
import { STATUS_TITLES } from '@/utilities/appConsts'
import { COLLINS_PHM_TIER_3 } from '@/utilities/appConsts'
import moment from 'moment'

import {
  AscMxRecPropsMixin,
  AscMxRecPermissionMixin,
} from '@/components/mixins'

export default {
  name: 'AscMxRecDetail',

  components: {
    AscAutocompleteSelector,
    AscLinkButton,
  },

  mixins: [AscMxRecPropsMixin, AscMxRecPermissionMixin],

  props: {
    data: {
      type: Object,
      required: true,
    },
    severityOptions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: true,
    },
    isSupportInfoLoading: {
      type: Boolean,
      default: false,
    },
    isCustomAlert: {
      type: Boolean,
      default: false,
    },
    airlineTierTypeId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      severity: this.data.severityId,
      supportInfoFlag: false,
    }
  },

  computed: {
    hasAlerts() {
      return this.data?.alerts?.length
    },
    showAlertDetails() {
      return this.hasAlerts || this.data.isManuallyCreated
    },
    isEmpty() {
      return !this.loading && this.data?.length
    },
    tail() {
      return this.data?.tail?.name || 'N/A'
    },
    model() {
      return this.data?.model?.name || 'N/A'
    },
    system() {
      return this.isCustomAlert ? '-' : this.data?.system?.name || 'N/A'
    },
    component() {
      if (
        this.isCustomAlert ||
        (this.data?.positionName && this.data?.component?.name)
      ) {
        return this.isCustomAlert
          ? '-'
          : this.data.component.name + ` (${this.data.positionName})`
      }
      return this.data?.component?.name || 'N/A'
    },
    analyticModuleName() {
      return this.data?.analyticModuleName || 'N/A'
    },
    firstAlertDate() {
      return moment(this.data.alerts[0].date).format('DD MMM YYYY')
    },

    getCustomAlertSeverity() {
      return STATUS_TITLES[this.data?.customAlertSeverityLevel]
    },

    getSeverity: {
      get() {
        return this.data.severityId
      },
      set(val) {
        this.severity = val
        this.$emit('updateSeverity', val)
      },
    },
    earliestAlert() {
      if (this.hasAlerts) {
        const minDate = new Date(
          Math.min(...this.data.alerts.map(item => new Date(item.date))),
        )
        const minDateObject = this.data.alerts.filter(item => {
          const d = new Date(item.date)
          return d.getTime() === minDate.getTime()
        })[0]
        return minDateObject.alert ? minDateObject.alert : 'N/A'
      }
      return 'N/A'
    },
    showViewAlertsLink() {
      if (this.airlineTierTypeId === COLLINS_PHM_TIER_3) {
        return (
          !this.isMxRecEditPermission &&
          this.hasAlerts &&
          !this.isSupportInfoLoading
        )
      } else {
        return (
          !this.isMxRecEditPermission &&
          this.hasAlerts &&
          !this.isSupportInfoLoading &&
          !this.isRecommendationTab
        )
      }
    },
  },

  methods: {
    handleViewPHMAlerts(e) {
      this.$emit('viewPHMAlerts', e)
    },
  },
}
</script>

<style lang="scss">
.asc-issue-detail--table {
  display: flex;
  max-width: 800px;

  &:first-child {
    flex: 2;
  }

  &-column {
    flex: 3;

    &-header {
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 1px;
      line-height: 18px;
      color: $primary-black;
      padding-bottom: 16px;
      margin-bottom: 12px;
    }

    &-list {
      list-style: none;
      padding: 0 !important;

      .asc-issue-detail--detailItem {
        display: flex;
        margin-bottom: 24px;
        flex-wrap: wrap;
        padding-right: 8px;
        word-break: break-word;
      }

      li {
        font-size: $font-size-md;
        display: flex;
        margin-bottom: 28px;

        .label {
          color: $gray-700;
          flex: 2 1;
        }

        .detail {
          color: $primary-black;
          flex: 4;

          &--link-button {
            margin-left: 12px;
          }
        }

        &:last-child {
          margin-bottom: 12px;
        }
      }
    }
  }
}

@media only screen and (max-width: 1000px) {
  .asc-issue-detail {
    .asc-issue-detail--table-column-header {
      margin-bottom: 8px;
    }
  }
}

@media only screen and (max-width: 800px) {
  .asc-issue-detail {
    .asc-issue-detail--detailItem {
      flex-direction: column;
    }
  }
}

@media only screen and (max-width: 612px) {
  .asc-issue-detail {
    .asc-issue-detail--table {
      flex-direction: column;
    }

    /* for really small screens stack the columns
    ,remove custom width from first column and add bottom margin */
    .asc-issue-detail--table-column {
      flex: none;
      width: 100%;
      margin-bottom: $size-medium;
    }
  }
}
</style>
